import {AxiosResponse} from "axios";
import {VidalInteraction} from "@/drug-interaction/interfaces";
import {VidalAllergy, VidalPathology} from "@/customer/interfaces/Patient";
import {InteractionAnalysis} from "@/drug-interaction/interfaces";

/**
 * The drug interaction resource.
 *
 * @param {Object} client - The injected client.
 * @param {Object} hdsClient - The injected hds client.
 * @returns {Object} The public object.
 */
export default function (client, hdsClient) {
    return {
        /**
         * Get drug interaction link based on product cips.
         * @param productCips
         * @param customerId
         */
        async read(productCips: string[], customerId: number): Promise<{prisca_path: string}> {
            return hdsClient.get(`vidal/customers/${customerId}`, {params: {ids: productCips}})
                .then((response: AxiosResponse) => response.data.data);
        },

        /**
         * Get drug interactions based on product codes and customer declared information.
         */
        async interactions(codes: string[], customerId: number): Promise<VidalInteraction[]> {
            return hdsClient.get(`vidal/customers/${customerId}/interactions`, {params: {codes}})
                .then((response: AxiosResponse) => response.data.data);
        },

        async HTMLInteractions(codes: string[], customerId: number): Promise<string> {
            return hdsClient.get(`vidal/customers/${customerId}/interactions/html`, {params: {codes}})
                .then((response: AxiosResponse) => response.data);
        },

        /**
         * Get analysis for a consumer based on product ids.
         */
        async analysis(productIds: number[], consumerKey: string): Promise<InteractionAnalysis> {
            return client.get(`vidal/consumers/${consumerKey}/analysis`, {params: {products: productIds}})
                .then((response: AxiosResponse) => response.data);
        },

        /**
         * Get html analysis for a consumer based on product ids.
         */
        async htmlAnalysis(productIds: number[], consumerKey: string): Promise<string> {
            return client.get(`vidal/consumers/${consumerKey}/html-analysis`, {params: {products: productIds}})
                .then((response: AxiosResponse) => response.data);
        },

        /**
         * Register vidal pathologies and allergies for a customer.
         * @param customerId
         * @param allergies
         * @param pathologies
         */
        async storeProfile(customerId: number, allergies: VidalAllergy[], pathologies: VidalPathology[]) {
            const data = {allergies, pathologies};
            return hdsClient.post(`vidal/customers/${customerId}/profile`, {data})
                .then(response => response.data.data);
        },
        /**
         * Get vidal pathologies based on search query.
         * @param search
         */
        async pathologies(search: string): Promise<Omit<VidalPathology, 'id'>[]> {
            return client.get('vidal/pathologies', {params: {search}})
                .then((response: AxiosResponse) => response.data.data);
        },
        /**
         * Get vidal allergies based on search query.
         * @param search
         */
        async allergies(search: string): Promise<Omit<VidalAllergy, 'id'>[]> {
            return client.get('vidal/allergies', {params: {search}})
                .then((response: AxiosResponse) => response.data.data);
        },
        /**
         * Get vidal side effects based on search query.
         * @param search
         */
        async sideEffects(search: string): Promise<{vidal_id: string, title: string}[]> {
            return client.get('vidal/side-effects', {params: {search}})
                .then((response: AxiosResponse) => response.data.data);
        }
    };
}
